// Product Control Plane — the publication trail.
//
// REFUSALS ARE SHOWN, not filtered out. "Someone tried to publish a
// credential-bearing URL and the server refused" is the entry that matters
// most, and a timeline that only lists successes is a timeline that hides
// exactly the events worth reading.
//
// The trail carries a wallet address or a public key id and nothing else — no
// session hash, no bearer token, no HMAC secret, no request body. That is
// enforced server-side; this screen simply has nothing else to render.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { listAudit, type AuditEvent } from "../controlPlane/api";
import { useControlPlaneTheme } from "../controlPlane/theme";
import {
  Btn,
  Card,
  CpHeader,
  Empty,
  ErrorNote,
  Hash,
  StatusChip,
} from "../controlPlane/ui";
import { type StatusTone } from "../controlPlane/styles";

function describeError(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

const ACTION_TONE: Record<AuditEvent["action"], StatusTone> = {
  DRAFT_CREATED: "muted",
  DRAFT_UPDATED: "muted",
  DRAFT_RESET: "muted",
  VALIDATED: "muted",
  PUBLISHED: "ok",
  ROLLED_BACK: "info",
};

const RESULT_TONE: Record<AuditEvent["result"], StatusTone> = {
  ok: "ok",
  rejected: "danger",
  error: "danger",
};

export function ControlPlaneAudit() {
  const { productId = "" } = useParams();
  const { themeProps, toggle, theme } = useControlPlaneTheme();

  const [events, setEvents] = useState<AuditEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [onlyChanges, setOnlyChanges] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await listAudit(productId);
      setEvents(r.events);
    } catch (e) {
      setError(describeError(e));
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    void load();
  }, [load]);

  const shown = useMemo(() => {
    if (!events) return [];
    return onlyChanges
      ? events.filter((e) => e.action === "PUBLISHED" || e.action === "ROLLED_BACK")
      : events;
  }, [events, onlyChanges]);

  return (
    <div {...themeProps} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <CpHeader
        breadcrumb={
          <>
            <Link to="/control-plane">Control Plane</Link>
            {" / "}
            <Link to={`/control-plane/${productId}`}>{productId}</Link>
          </>
        }
        title="Audit"
        subtitle="publication trail — newest first"
        actions={
          <>
            <Btn
              tone={onlyChanges ? "primary" : "default"}
              onClick={() => setOnlyChanges((v) => !v)}
            >
              {onlyChanges ? "Showing live changes only" : "Show live changes only"}
            </Btn>
            <button type="button" onClick={toggle}>
              {theme === "light" ? "Dark" : "Light"}
            </button>
            <button type="button" onClick={() => void load()} disabled={loading}>
              {loading ? "Loading…" : "Refresh"}
            </button>
          </>
        }
      />

      {error !== null && <ErrorNote>{error}</ErrorNote>}

      <Card style={{ padding: 0 }}>
        {loading && events === null ? (
          <Empty>Loading…</Empty>
        ) : shown.length === 0 ? (
          <Empty>No events.</Empty>
        ) : (
          <ol
            style={{
              listStyle: "none",
              margin: 0,
              padding: "8px 16px 16px",
              display: "grid",
              gridTemplateColumns: "max-content 1fr",
              columnGap: 16,
            }}
          >
            {shown.map((e) => (
              <li key={e.id} style={{ display: "contents" }}>
                <time
                  dateTime={e.occurredAt}
                  style={{
                    color: "var(--fg-dim)",
                    padding: "10px 0",
                    borderTop: "1px solid var(--border-faint, #20262e)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {e.occurredAt}
                </time>
                <div
                  style={{
                    padding: "10px 0",
                    borderTop: "1px solid var(--border-faint, #20262e)",
                    minWidth: 0,
                  }}
                >
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <StatusChip tone={e.result === "ok" ? ACTION_TONE[e.action] : RESULT_TONE[e.result]}>
                      {e.action}
                    </StatusChip>
                    {e.result !== "ok" && (
                      <StatusChip tone={RESULT_TONE[e.result]}>{e.result.toUpperCase()}</StatusChip>
                    )}
                    {e.fromRevision && e.toRevision && (
                      <span style={{ color: "var(--fg-dim)" }}>
                        {e.fromRevision} → <b style={{ color: "var(--fg)" }}>{e.toRevision}</b>
                      </span>
                    )}
                    {!e.fromRevision && e.toRevision && (
                      <span style={{ color: "var(--fg-dim)" }}>
                        → <b style={{ color: "var(--fg)" }}>{e.toRevision}</b>
                      </span>
                    )}
                  </div>
                  <div style={{ color: "var(--fg-dim)", marginTop: 3 }}>
                    {e.actor}
                    {e.actorLabel ? ` · ${e.actorLabel}` : ""}
                    {e.surfaceId ? ` · ${e.surfaceId}` : ""}
                  </div>
                  {e.detail && (
                    <div
                      style={{
                        color: e.result === "ok" ? "var(--fg-dim)" : "var(--danger)",
                        marginTop: 2,
                      }}
                    >
                      {e.detail}
                    </div>
                  )}
                  {e.documentHash && (
                    <div style={{ marginTop: 2 }}>
                      <Hash value={e.documentHash} />
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </Card>

      <Card title="What is recorded">
        <div style={{ color: "var(--fg-dim)" }}>
          Two trails exist and answer different questions.{" "}
          <b style={{ color: "var(--fg)" }}>This one</b> is the publication
          record: which revision replaced which, when, by whom, with what hash.
          A separate access trail records which authenticated caller reached
          which route with what decision. An access log alone cannot answer
          &ldquo;what was live last Tuesday&rdquo; without reconstructing it
          from redacted request bodies, which is why both exist.
        </div>
      </Card>
    </div>
  );
}
